import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiFetch } from '@/lib/api-client';
import { phCapture, phIdentify, phReset } from '@/lib/analytics/posthog';
import { clearStoredUtms, readStoredUtms } from '@/lib/utm';
import type {
  AuthTokens,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  RegisterResponse,
  ResetPasswordRequest,
  UpdateUserRequest,
  User,
  VerifyEmailRequest,
} from '@/lib/auth-types';

export type AuthStatus = 'idle' | 'authenticating' | 'authenticated' | 'error';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  status: AuthStatus;

  setTokens: (accessToken: string, refreshToken: string) => void;
  /**
   * Idempotent token-replacement after backend operations that issue
   * a fresh TokenPair (e.g. POST /api/auth/change-password — Batch 9B
   * Commit 5). Wraps `setTokens` with the AuthTokens object shape so
   * call-sites don't have to destructure.
   */
  replaceTokens: (pair: AuthTokens) => void;
  setUser: (user: User) => void;
  setStatus: (status: AuthStatus) => void;
  clearAuth: () => void;

  login: (payload: LoginRequest) => Promise<void>;
  register: (payload: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  updateProfile: (patch: UpdateUserRequest) => Promise<void>;
  requestEmailVerification: () => Promise<void>;
  verifyEmail: (payload: VerifyEmailRequest) => Promise<void>;
  requestPasswordReset: (payload: ForgotPasswordRequest) => Promise<void>;
  confirmPasswordReset: (payload: ResetPasswordRequest) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      status: 'idle',

      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      replaceTokens: (pair) =>
        set({ accessToken: pair.accessToken, refreshToken: pair.refreshToken }),
      setUser: (user) => set({ user }),
      setStatus: (status) => set({ status }),
      clearAuth: () => {
        set({ user: null, accessToken: null, refreshToken: null, status: 'idle' });
        // Token-refresh-failure path clears auth without going through logout()
        // — reset PostHog too so a session-restored (identified) user is not left
        // attributed on a shared device. Guarded no-op if never identified.
        phReset();
      },

      login: async (payload) => {
        set({ status: 'authenticating' });
        try {
          const tokens = await apiFetch<AuthTokens>('/api/auth/login', {
            method: 'POST',
            body: payload,
            skipAuth: true,
          });
          set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
          const user = await apiFetch<User>('/api/users/me');
          set({ user, status: 'authenticated' });
          phIdentify(user.id, user.email);
        } catch (err) {
          set({ status: 'error' });
          throw err;
        }
      },

      register: async (payload) => {
        set({ status: 'authenticating' });
        // Marketing attribution (2026-07-17). Forward any UTMs the LP →
        // signup hop captured into sessionStorage. Absent UTMs are omitted
        // (readStoredUtms returns only present keys), so the DTO's optional
        // fields stay clean for direct-to-app signups.
        const storedUtms = readStoredUtms();
        const payloadWithUtms = { ...storedUtms, ...payload };
        try {
          const res = await apiFetch<RegisterResponse>('/api/auth/register', {
            method: 'POST',
            body: payloadWithUtms,
            skipAuth: true,
          });
          set({ accessToken: res.accessToken, refreshToken: res.refreshToken });
          const user = await apiFetch<User>('/api/users/me');
          set({ user, status: 'authenticated' });
          phIdentify(user.id, user.email);
          // Fire signup_completed with the forwarded UTMs as event
          // properties, so the funnel event-side matches the person-side
          // properties PostHog's initAnalytics already sets via $set_once
          // + $set (see lib/analytics/posthog.ts). Consent-gated no-op when
          // PostHog isn't initialised. Spread into a fresh object so the
          // payload matches phCapture's Record<string, unknown> signature.
          phCapture('signup_completed', { ...storedUtms });
          // Attribution has landed on the user row — drop the blob so a
          // second signup in the same tab re-captures freshly from the URL.
          clearStoredUtms();
        } catch (err) {
          set({ status: 'error' });
          throw err;
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        if (refreshToken) {
          try {
            await apiFetch('/api/auth/logout', {
              method: 'POST',
              body: { refreshToken },
            });
          } catch {
            // Best-effort — client-side logout proceeds regardless
          }
        }
        set({ user: null, accessToken: null, refreshToken: null, status: 'idle' });
        phReset();
      },

      fetchCurrentUser: async () => {
        const user = await apiFetch<User>('/api/users/me');
        set({ user, status: 'authenticated' });
        // Returning user via persisted-session restore (no fresh login) —
        // re-identify so analytics attaches to the backend user id.
        phIdentify(user.id, user.email);
      },

      updateProfile: async (patch) => {
        const partial = await apiFetch<Partial<User>>('/api/users/me', {
          method: 'PATCH',
          body: patch,
        });
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
        }));
      },

      requestEmailVerification: async () => {
        await apiFetch<{ message: string }>('/api/auth/request-verification', {
          method: 'POST',
        });
      },

      verifyEmail: async (payload) => {
        await apiFetch<{ message: string }>('/api/auth/verify-email', {
          method: 'POST',
          body: payload,
          skipAuth: true,
        });
        const { accessToken } = get();
        if (accessToken) {
          const user = await apiFetch<User>('/api/users/me');
          set({ user });
        }
      },

      requestPasswordReset: async (payload) => {
        await apiFetch<{ message: string }>('/api/auth/forgot-password', {
          method: 'POST',
          body: payload,
          skipAuth: true,
        });
      },

      confirmPasswordReset: async (payload) => {
        await apiFetch<{ message: string }>('/api/auth/reset-password', {
          method: 'POST',
          body: payload,
          skipAuth: true,
        });
      },
    }),
    {
      name: 'dreamable-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);
